import { useQuery, useMutation, useQueryClient, UseQueryOptions } from '@tanstack/react-query'

const API_BASE_URL = '/api'

// Error types
export interface ApiError {
  message: string
  status: number
  details?: Record<string, string[]>
}

export class ApiException extends Error {
  constructor(
    public status: number,
    message: string,
    public details?: Record<string, string[]>
  ) {
    super(message)
    this.name = 'ApiException'
  }
}

// Generic API client with enhanced error handling
async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`
  
  try {
    const response = await fetch(url, {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    })

    if (!response.ok) {
      let errorData: { message?: string; error?: string; details?: Record<string, string[]> } = {}
      try {
        errorData = await response.json()
      } catch {
        // If response is not JSON, use default error
        errorData = { message: `HTTP ${response.status}: ${response.statusText}` }
      }

      throw new ApiException(
        response.status,
        errorData.message || errorData.error || `HTTP ${response.status}: ${response.statusText}`,
        errorData.details
      )
    }

    // Handle empty responses (like DELETE operations)
    const contentType = response.headers.get('content-type')
    if (contentType && contentType.includes('application/json')) {
      return response.json()
    } else {
      return {} as T
    }
  } catch (error) {
    if (error instanceof ApiException) {
      throw error
    }
    
    // Network or other errors
    throw new ApiException(
      0,
      error instanceof Error ? error.message : 'Network error occurred'
    )
  }
}

// Enhanced custom hooks for common API patterns
export function useApiQuery<T>(
  key: string[],
  endpoint: string,
  options?: Partial<UseQueryOptions<T, ApiException>>
) {
  return useQuery({
    queryKey: key,
    queryFn: () => apiRequest<T>(endpoint),
    retry: (failureCount, error) => {
      // Don't retry on client errors (4xx)
      if (error instanceof ApiException && error.status >= 400 && error.status < 500) {
        return false
      }
      return failureCount < 3
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    ...options,
  })
}

export function useApiMutation<TData, TVariables = void, TQueryData = unknown>(
  endpoint: string | ((variables: TVariables) => string),
  method: 'POST' | 'PUT' | 'DELETE' | 'PATCH' = 'POST',
  options?: {
    onSuccess?: (data: TData, variables: TVariables) => void
    onError?: (error: ApiException, variables: TVariables) => void
    invalidateQueries?: string[][]
    optimisticUpdate?: {
      queryKey: string[]
      updater: (oldData: TQueryData | undefined, variables: TVariables) => TQueryData
    }
  }
) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (variables: TVariables) => {
      const url = typeof endpoint === 'function' ? endpoint(variables) : endpoint
      return apiRequest<TData>(url, {
        method,
        body: method !== 'DELETE' ? JSON.stringify(variables) : undefined,
      })
    },
    onMutate: async (variables) => {
      // Optimistic update
      if (options?.optimisticUpdate) {
        await queryClient.cancelQueries({ queryKey: options.optimisticUpdate.queryKey })
        const previousData = queryClient.getQueryData(options.optimisticUpdate.queryKey)
        queryClient.setQueryData(
          options.optimisticUpdate.queryKey,
          options.optimisticUpdate.updater(previousData as TQueryData | undefined, variables)
        )
        return { previousData }
      }
    },
    onSuccess: (data, variables) => {
      options?.onSuccess?.(data, variables)
      options?.invalidateQueries?.forEach((queryKey) => {
        queryClient.invalidateQueries({ queryKey })
      })
    },
    onError: (error, variables, context) => {
      // Rollback optimistic update on error
      if (options?.optimisticUpdate && context?.previousData) {
        queryClient.setQueryData(options.optimisticUpdate.queryKey, context.previousData)
      }
      options?.onError?.(error as ApiException, variables)
    },
  })
}

// Specialized hooks for common patterns
export function useOptimisticMutation<TData, TVariables = void, TQueryData = unknown>(
  endpoint: string | ((variables: TVariables) => string),
  queryKey: string[],
  updater: (oldData: TQueryData | undefined, variables: TVariables) => TQueryData,
  method: 'POST' | 'PUT' | 'DELETE' | 'PATCH' = 'POST'
) {
  return useApiMutation<TData, TVariables, TQueryData>(endpoint, method, {
    optimisticUpdate: { queryKey, updater },
    invalidateQueries: [queryKey]
  })
}

export { apiRequest }